import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, CartProvider, useAuth } from './lib/store';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import CartPage from './pages/CartPage';
import QuotationsPage from './pages/QuotationsPage';
import QuotationDetailPage from './pages/QuotationDetailPage';
import CustomerProfilePage from './pages/CustomerProfilePage';
import AdminPage from './pages/AdminPage';
import AIImportPage from './pages/AIImportPage';
import StockManagementPage from './pages/StockManagementPage';
import DeliveryPage from './pages/DeliveryPage';
import PurchaseOrderPage from './pages/PurchaseOrderPage';
import StockHistoryPage from './pages/StockHistoryPage';
import QrCodesPage from './pages/QrCodesPage';
import QrViewPage from './pages/QrViewPage';
import MyAnalyticsPage from './pages/MyAnalyticsPage';
import MyRegistrationsPage from './pages/MyRegistrationsPage';
import MyComplaintsPage from './pages/MyComplaintsPage';
import TeamFollowupsPage from './pages/TeamFollowupsPage';
import MyPendingQuotesPage from './pages/MyPendingQuotesPage';
import CatalogHealthPage from './pages/CatalogHealthPage';
import ComparisonAnalyticsPage from './pages/ComparisonAnalyticsPage';
import './App.css';
import { Loader2 } from 'lucide-react';

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-heda-cream" aria-label="Loading">
    <Loader2 className="h-6 w-6 animate-spin text-heda-gold" />
  </div>
);

// Only same-site paths after login ("/x", not "//evil.com" or "https://...").
const safeRedirect = (r) => (r && r.startsWith('/') && !r.startsWith('//') ? r : '/');

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) {
    // Preserve the original path so users land back here after logging in
    // (matters for /qr/:id deep links from scanned stickers).
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={safeRedirect(new URLSearchParams(window.location.search).get('redirect'))} replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Layout><ProductsPage /></Layout></ProtectedRoute>} />
      <Route path="/cart" element={<ProtectedRoute><Layout><CartPage /></Layout></ProtectedRoute>} />
      <Route path="/quotations" element={<ProtectedRoute><Layout><QuotationsPage /></Layout></ProtectedRoute>} />
      <Route path="/quotations/:id" element={<ProtectedRoute><Layout><QuotationDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><Layout><CustomerProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Layout><AdminPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/comparison-analytics" element={<ProtectedRoute><Layout><ComparisonAnalyticsPage /></Layout></ProtectedRoute>} />
      <Route path="/stock" element={<ProtectedRoute><Layout><StockManagementPage /></Layout></ProtectedRoute>} />
      <Route path="/stock/history" element={<ProtectedRoute><Layout><StockHistoryPage /></Layout></ProtectedRoute>} />
      <Route path="/delivery" element={<ProtectedRoute><Layout><DeliveryPage /></Layout></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute><Layout><PurchaseOrderPage /></Layout></ProtectedRoute>} />
      <Route path="/ai-import" element={<ProtectedRoute><AIImportPage /></ProtectedRoute>} />
      <Route path="/qr-codes" element={<ProtectedRoute><Layout><QrCodesPage /></Layout></ProtectedRoute>} />
      <Route path="/qr/:id" element={<ProtectedRoute><QrViewPage /></ProtectedRoute>} />
      <Route path="/my-analytics" element={<ProtectedRoute><Layout><MyAnalyticsPage /></Layout></ProtectedRoute>} />
      <Route path="/my-registrations" element={<ProtectedRoute><Layout><MyRegistrationsPage /></Layout></ProtectedRoute>} />
      <Route path="/my-complaints" element={<ProtectedRoute><Layout><MyComplaintsPage /></Layout></ProtectedRoute>} />
      <Route path="/team-followups" element={<ProtectedRoute><Layout><TeamFollowupsPage /></Layout></ProtectedRoute>} />
      <Route path="/my-pending-quotes" element={<ProtectedRoute><Layout><MyPendingQuotesPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/catalog-health" element={<ProtectedRoute><Layout><CatalogHealthPage /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Toasts sit top-center on every screen: clear of the bottom nav on phones
// and of the quotation builder's Save / PDF buttons, which sit at the
// bottom of the screen on desktop and tablet.
const TOAST_POSITION = 'top-center';

function App() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL || undefined}>
      <AuthProvider>
        <CartProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <Toaster position={TOAST_POSITION} richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
